'use client';

import React, { useState, useEffect, useMemo } from 'react';
import Head from 'next/head';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';

interface FaqItem {
  id: string;
  cat: string;
  q: string;
  updated: string;
  votes: {
    up: number;
    down: number;
  };
  a: string;
}

const INITIAL_FAQ_DATA: FaqItem[] = [
  { id: 'f1', cat: 'Общие', q: 'Что такое INTERIM и чем вы занимаетесь?', updated: '2025-08-10', votes: { up: 38, down: 2 }, a: `Мы — сервис решений в рекрутменте: <b>подбор персонала (RPO)</b>, <b>аутсорсинг</b> и <b>аутстаффинг</b>. Фокус — скорость закрытия, прозрачные KPI/SLA и отчётность в BI.` },
  { id: 'f2', cat: 'Услуги', q: 'В чём разница между подбором, аутсорсингом и аутстаффингом?', updated: '2025-08-09', votes: { up: 42, down: 3 }, a: `<ul class="list-disc ml-6"><li><b>Подбор (RPO)</b> — мы закрываем вакансии под ваши KPI.</li><li><b>Аутсорсинг</b> — берём на себя функцию (например, линия L1/L2).</li><li><b>Аутстаффинг</b> — оформляем сотрудников у нас и выводим в ваш проект.</li></ul>` },
  { id: 'f3', cat: 'Цены', q: 'Как считается стоимость услуг?', updated: '2025-08-11', votes: { up: 51, down: 4 }, a: `Для быстрого расчёта используем базовые ставки: <b>Подбор — 20%</b>, <b>Аутсорсинг — 25%</b>, <b>Аутстаффинг — 15%</b>. Возможны пакеты: фикс + бонус, скидки за объём, MSP.` },
  { id: 'f4', cat: 'Услуги', q: 'Какие сроки закрытия вакансий?', updated: '2025-08-12', votes: { up: 29, down: 6 }, a: `Типовой коридор: массовые роли — <b>3–14 дней</b>, специалисты — <b>2–5 недель</b>. На старте фиксируем SLA по воронке и времени цикла, ежедневно даём отчёт.` },
  { id: 'f5', cat: 'Сотрудничество', q: 'Есть ли гарантийная замена?', updated: '2025-08-05', votes: { up: 33, down: 1 }, a: `Да. Период замены зависит от роли и пакета. Обычно <b>30–60 дней</b> для массовых позиций и <b>60–90</b> — для специалистов.` },
  { id: 'f6', cat: 'Технологии', q: 'С какими системами вы интегрируетесь?', updated: '2025-08-08', votes: { up: 27, down: 2 }, a: `Подключаемся к вашей <b>ATS/CRM</b>, календарям, почте, BI (Power BI/Looker Studio). Есть шаблоны выгрузок CSV/Excel и API.` },
  { id: 'f7', cat: 'Безопасность', q: 'Как вы защищаете персональные данные?', updated: '2025-08-01', votes: { up: 36, down: 0 }, a: `Доступы по принципу наименьших прав, NDA, шифрование каналов. Практики соответствуют <b>ISO/IEC 27001</b>.` },
  { id: 'f8', cat: 'Кандидаты', q: 'Где смотреть актуальные вакансии?', updated: '2025-08-07', votes: { up: 18, down: 3 }, a: `Раздел «Решения → Соискателям → Актуальные вакансии». Также публикуем в Telegram/HH — ссылки в футере.` },
  { id: 'f9', cat: 'Цены', q: 'Можно ли зафиксировать бюджет и платить за результат?', updated: '2025-08-03', votes: { up: 21, down: 4 }, a: `Да. Подберём пакет: <b>фикс + успех</b>, поэтапная оплата по SLA, либо MSP-модель для мультивендора.` },
  { id: 'f10', cat: 'Сотрудничество', q: 'Работаете ли вы по модели MSP?', updated: '2025-08-10', votes: { up: 25, down: 1 }, a: `Да. Объединяем нескольких провайдеров в единый пул, выравниваем KPI, даём сводную отчётность и контроль качества.` },
  { id: 'f11', cat: 'Технологии', q: 'Вы даёте доступ к дашбордам?', updated: '2025-08-06', votes: { up: 22, down: 0 }, a: `Да. Личный кабинет/BI-дашборд: воронка, конверсии, скорость, CPH, явка, рекрутер-на-вакансию, источники.` },
  { id: 'f12', cat: 'Общие', q: 'Где вы работаете географически и на каких языках?', updated: '2025-08-02', votes: { up: 16, down: 1 }, a: `Основные регионы: СНГ и ЕС. Русский и английский, по запросу — локализация рекрутинга под рынок.` },
  { id: 'f13', cat: 'Кандидаты', q: 'Как подать резюме и получить обратную связь?', updated: '2025-08-04', votes: { up: 14, down: 2 }, a: `Через форму в разделе «Соискателям» или по e-mail. Отправляем статус и рекомендации, если нужна доработка резюме.` },
  { id: 'f14', cat: 'Безопасность', q: 'Можем ли мы запросить удаление данных?', updated: '2025-08-12', votes: { up: 30, down: 0 }, a: `Да. По запросу удаляем личные данные согласно политике хранения и требованиям законодательства.` },
];

export default function Home() {
  const [faqData, setFaqData] = useState(INITIAL_FAQ_DATA);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('pop');
  const [votedItems, setVotedItems] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    topic: 'Общие',
    question: '',
  });
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (status) {
      setStatus(null);
    }
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
    if (e.target.checked && status?.includes('согласитесь с обработкой')) {
      setStatus(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isConsentChecked) {
      setStatus('Пожалуйста, согласитесь с обработкой персональных данных.');
      return;
    }

    setSubmitting(true);
    setStatus('Отправка...');

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            formName: "FAQ",
            name: formData.name,
            email: formData.email,
            topic: formData.topic,
            question: formData.question,
          }
        }),
      });

      if (response.ok) {
        setStatus('Спасибо! Ваш вопрос отправлен.');
        setFormData({
          name: '',
          email: '',
          topic: 'Общие',
          question: '',
        });
        setIsConsentChecked(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || 'Неизвестная ошибка сервера.';
        setStatus(`Ошибка: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Ошибка при отправке формы:', error);
      setStatus('Произошла ошибка сети. Пожалуйста, проверьте подключение.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const votedState = JSON.parse(localStorage.getItem('votedItems') || '{}');
    setVotedItems(votedState);
  }, []);

  useEffect(() => {
    const openHash = () => {
      const h = window.location.hash.replace('#', '');
      if (h) {
        const el = document.getElementById(h);
        if (el && el.tagName.toLowerCase() === 'details') {
          el.setAttribute('open', 'open');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    };
    window.addEventListener('hashchange', openHash);
    openHash();
    return () => window.removeEventListener('hashchange', openHash);
  }, []);

  const filteredItems = useMemo(() => {
    let items = faqData.filter(x => {
      const hay = (x.q + ' ' + x.a).toLowerCase();
      return (!query || hay.includes(query.toLowerCase())) && (!category || x.cat === category);
    });

    switch (sortBy) {
      case 'pop':
        items.sort((a, b) => (b.votes.up - b.votes.down) - (a.votes.up - a.votes.down));
        break;
      case 'date':
        items.sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime());
        break;
      case 'az':
        items.sort((a, b) => a.q.localeCompare(b.q, 'ru'));
        break;
    }
    return items;
  }, [faqData, query, category, sortBy]);

  const handleVote = (id: string, val: number) => {
    if (votedItems[id]) {
      alert('Вы уже голосовали за этот ответ.');
      return;
    }

    const updatedFaqData = faqData.map(item => {
      if (item.id === id) {
        return {
          ...item,
          votes: {
            ...item.votes,
            up: val > 0 ? item.votes.up + 1 : item.votes.up,
            down: val < 0 ? item.votes.down + 1 : item.votes.down,
          },
        };
      }
      return item;
    });

    setFaqData(updatedFaqData);

    const newVotedItems = { ...votedItems, [id]: true };
    setVotedItems(newVotedItems);
    localStorage.setItem('votedItems', JSON.stringify(newVotedItems));
  };

  const copyLink = (id: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url).then(() => alert('Ссылка скопирована'));
  };

  return (
    <>
      <Head>
        <title>INTERIM — FAQ</title>
        <meta name="description" content="Короткие ответы на частые вопросы о сервисах: подбор, аутсорсинг, аутстаффинг, цены, технологии и безопасность." />
        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet" />
        <style>
          {`
            :root{ --accent:#FFC300; --card:rgba(255,255,255,.08); --chip:rgba(255,255,255,.14) }
            .card{background:var(--card); backdrop-filter:blur(8px)}
            .chip{background:var(--chip)}
            .btn{border-radius:.75rem; padding:.7rem 1rem; font-weight:700}
            .btn-accent{background:var(--accent); color:#102a43}
            .btn-ghost{background:rgba(255,255,255,.10)}
            .btn-ghost:hover{background:rgba(255,255,255,.20)}
            .tag{background:rgba(255,255,255,.12); padding:.25rem .6rem; border-radius:999px; font-size:.75rem}
            .link{color:#FFE07A; text-decoration:underline}
            details[open] summary .chev{transform:rotate(90deg)}
            .prose-invert h1, .prose-invert h2, .prose-invert h3, .prose-invert h4, .prose-invert h5, .prose-invert h6 { color: white; }
            .prose-invert li { color: #d1d5db; }
          `}
        </style>
        <script
          id="ldjson"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": filteredItems.slice(0, 12).map(x => ({
                "@type": "Question",
                "name": x.q,
                "acceptedAnswer": { "@type": "Answer", "text": x.a.replace(/<[^>]+>/g, '') }
              }))
            })
          }}
        />
      </Head>

      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 antialiased">
        <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
          style={{
            background: `linear-gradient(rgba(0,0,0,.45),rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)`
          }}>
          <div className="max-w-7xl mx-auto">
            <nav className="mb-6 flex flex-wrap gap-3 text-sm">
              <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">Вопросы</a>
              <a href="#filters" className="chip px-3 py-1 rounded-full hover:bg-white/25">Фильтры</a>
              <a href="#ask" className="chip px-3 py-1 rounded-full hover:bg-white/25">Задать вопрос</a>
            </nav>
            <h1 className="text-4xl md:text-6xl font-extrabold mb-4">FAQ INTERIM</h1>
            <p className="max-w-3xl text-lg md:text-xl text-gray-200">
              Короткие ответы на частые вопросы о сервисах: подбор, аутсорсинг, аутстаффинг, цены, технологии и безопасность.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#faq" className="btn btn-ghost">Перейти к вопросам</a>
              <button className="btn btn-accent" onClick={() => window.print()}>Печать/Сохранить в PDF</button>
            </div>
          </div>
        </header>

        <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
          <section id="filters" aria-label="Фильтры FAQ" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Найти ответ</h2>
            <div className="grid gap-3 md:grid-cols-6">
              <input
                id="q"
                className="rounded-xl px-4 py-3 text-gray-900 md:col-span-3"
                placeholder="Поиск по вопросу/ответу…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              <select
                id="cat"
                className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
                value={category}
                onChange={e => setCategory(e.target.value)}
              >
                <option value="">Категория: все</option>
                <option>Общие</option>
                <option>Услуги</option>
                <option>Цены</option>
                <option>Сотрудничество</option>
                <option>Кандидаты</option>
                <option>Технологии</option>
                <option>Безопасность</option>
              </select>
              <select
                id="sort"
                className="rounded-xl px-4 py-3 text-gray-900"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                <option value="pop">Сортировать: по полезности</option>
                <option value="date">по обновлению</option>
                <option value="az">по алфавиту</option>
              </select>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-sm">

              <button
                className="chip px-3 py-1 rounded-full hover:bg-white/25"
                onClick={() => {
                  setCategory('');
                  setQuery('');
                }}
              >
                🔄 Все
              </button>

              <button className="chip px-3 py-1 rounded-full hover:bg-white/25" onClick={() => setQuery('Оплата')}>Оплата</button>
              <button className="chip px-3 py-1 rounded-full hover:bg-white/25" onClick={() => setQuery('RPO')}>RPO</button>
              <button className="chip px-3 py-1 rounded-full hover:bg-white/25" onClick={() => setQuery('ATS')}>ATS</button>
              <button className="chip px-3 py-1 rounded-full hover:bg-white/25" onClick={() => setQuery('ISO')}>ISO</button>
              <button className="chip px-3 py-1 rounded-full hover:bg-white/25" onClick={() => setQuery('Гарантия')}>Гарантия</button>
            </div>
          </section>

          <section id="faq" aria-label="Список вопросов" className="scroll-mt-32">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h2 className="text-3xl md:text-4xl font-bold">Вопросы и ответы</h2>
              <span id="count" className="text-sm text-gray-300">Найдено вопросов: {filteredItems.length}</span>
            </div>
            <div id="list" className="mt-6 grid gap-3">
              {filteredItems.length > 0 ? (
                filteredItems.map(item => (
                  <details id={item.id} key={item.id} className="card rounded-xl p-4">
                    <summary className="cursor-pointer flex items-start gap-3">
                      <span className="chev mt-1 transition-transform">▶</span>
                      <div>
                        <div className="font-semibold text-lg">{item.q}</div>
                        <div className="mt-1 text-xs flex flex-wrap gap-2">
                          <span className="tag">{item.cat}</span>
                          <span className="tag">Обновлено: {new Date(item.updated).toLocaleDateString('ru-RU')}</span>
                          <span className="tag">Полезность: {item.votes.up - item.votes.down}</span>
                        </div>
                      </div>
                    </summary>
                    <div
                      className="mt-3 prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: item.a }}
                    />
                    <div className="mt-3 flex flex-wrap gap-2 text-sm">
                      <button className="btn btn-ghost" onClick={() => handleVote(item.id, 1)}>Полезно</button>
                      <button className="btn btn-ghost" onClick={() => handleVote(item.id, -1)}>Не полезно</button>
                      <button className="btn btn-ghost" onClick={() => copyLink(item.id)}>Скопировать ссылку</button>
                    </div>
                  </details>
                ))
              ) : (
                <p id="empty" className="text-gray-300">Ничего не найдено — измените фильтры.</p>
              )}
            </div>
          </section>

          <section id="ask" className="simple-card scroll-mt-32">
            <h3 className="text-2xl font-bold">Не нашли ответ?</h3>
            <p className="text-sm text-gray-200 mt-1">Задайте вопрос — мы ответим на e-mail и добавим в базу.</p>
            <form className="mt-4 grid gap-3 md:grid-cols-2" onSubmit={handleSubmit}>
              <input
                className="rounded-xl px-4 py-3 text-gray-900"
                placeholder="Имя*"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={submitting}
              />
              <input
                className="rounded-xl px-4 py-3 text-gray-900"
                placeholder="E-mail*"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={submitting}
              />
              <select
                className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
                name="topic"
                value={formData.topic}
                onChange={handleInputChange}
                disabled={submitting}
              >
                <option value="Общие">Тема: Общие</option>
                <option value="Услуги">Тема: Услуги</option>
                <option value="Цены">Тема: Цены</option>
                <option value="Сотрудничество">Тема: Сотрудничество</option>
                <option value="Кандидаты">Тема: Кандидаты</option>
                <option value="Технологии">Тема: Технологии</option>
                <option value="Безопасность">Тема: Безопасность</option>
              </select>
              <textarea
                className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
                rows={4}
                placeholder="Ваш вопрос*"
                name="question"
                value={formData.question}
                onChange={handleInputChange}
                required
                disabled={submitting}
              ></textarea>
              <CustomCheckbox
                name="consent"
                isChecked={isConsentChecked}
                onChange={handleConsentChange}
                link="/privacy"
                textColorClass="text-gray-400"
                linkColorClass="text-[#FF7C22]"
                className="md:col-span-2"
              />
              <button
                type="submit"
                className="btn btn-accent md:col-span-2 disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Отправка...' : 'Отправить'}
              </button>
              {status && (
                <div className={`mt-2 text-center md:col-span-2 text-sm ${status.startsWith('Ошибка') ? 'text-red-400' : 'text-green-300'}`}>
                  {status}
                </div>
              )}
            </form>
          </section>
        </main>
      </div>
    </>
  );
}